import { v4 as uuidv4 } from 'uuid';
import { authService } from './authService';

const POLLS_KEY = 'appointment_app_polls';

class PollService {
  constructor() {
    this.initializeStorage();
    // Clean up expired data on service initialization
    this.cleanupExpiredData();
  }

  initializeStorage() {
    if (!localStorage.getItem(POLLS_KEY)) {
      localStorage.setItem(POLLS_KEY, JSON.stringify([]));
    }
  }

  generateShareId() {
    return uuidv4().split('-')[0];
  }

  async createPoll(pollData) {
    const currentUser = await authService.getCurrentUser();
    if (!currentUser) throw new Error('Nicht authentifiziert');

    const polls = JSON.parse(localStorage.getItem(POLLS_KEY));

    const poll = {
      id: uuidv4(),
      ownerId: currentUser.id,
      shareId: this.generateShareId(),
      title: pollData.title,
      description: pollData.description,
      options: pollData.options.map(option => ({
        id: uuidv4(),
        date: option.date,
        time: option.time,
        duration: option.duration,
        votes: []
      })),
      settings: {
        allowMultipleVotes: pollData.settings?.allowMultipleVotes || false,
        requireName: pollData.settings?.requireName || true,
        requireEmail: pollData.settings?.requireEmail || false,
        deadline: pollData.settings?.deadline || null,
        isAnonymous: pollData.settings?.isAnonymous || false
      },
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    polls.push(poll);
    localStorage.setItem(POLLS_KEY, JSON.stringify(polls));
    return poll;
  }

  async getUserPolls() {
    const currentUser = await authService.getCurrentUser();
    if (!currentUser) return [];

    const polls = JSON.parse(localStorage.getItem(POLLS_KEY));
    return polls.filter(poll => poll.ownerId === currentUser.id && poll.isActive);
  }

  async getPollByShareId(shareId) {
    const polls = JSON.parse(localStorage.getItem(POLLS_KEY));
    const poll = polls.find(p => p.shareId === shareId && p.isActive);
    
    if (!poll) throw new Error('Umfrage nicht gefunden');

    // Return poll without sensitive owner information for public access
    const { ownerId, ...publicPoll } = poll;
    return publicPoll;
  }

  async getPollById(id) {
    const currentUser = await authService.getCurrentUser();
    if (!currentUser) throw new Error('Nicht authentifiziert');

    const polls = JSON.parse(localStorage.getItem(POLLS_KEY));
    const poll = polls.find(p => p.id === id && p.ownerId === currentUser.id);
    
    if (!poll) throw new Error('Umfrage nicht gefunden');
    return poll;
  }

  async updatePoll(id, updateData) {
    const currentUser = await authService.getCurrentUser();
    if (!currentUser) throw new Error('Nicht authentifiziert');

    const polls = JSON.parse(localStorage.getItem(POLLS_KEY));
    const pollIndex = polls.findIndex(p => p.id === id && p.ownerId === currentUser.id);
    
    if (pollIndex === -1) throw new Error('Umfrage nicht gefunden');

    // Ensure options have IDs
    if (updateData.options) {
      updateData.options = updateData.options.map(option => ({
        ...option,
        id: option.id || uuidv4(),
        votes: option.votes || []
      }));
    }

    polls[pollIndex] = {
      ...polls[pollIndex],
      ...updateData,
      updatedAt: new Date().toISOString()
    };

    localStorage.setItem(POLLS_KEY, JSON.stringify(polls));
    return polls[pollIndex];
  }

  async submitVote(shareId, selectedOptionId, voterData) {
    const polls = JSON.parse(localStorage.getItem(POLLS_KEY));
    const pollIndex = polls.findIndex(p => p.shareId === shareId && p.isActive);
    
    if (pollIndex === -1) throw new Error('Umfrage nicht gefunden');

    const poll = polls[pollIndex];

    // Check deadline
    if (poll.settings.deadline && new Date() > new Date(poll.settings.deadline)) {
      throw new Error('Abstimmung ist abgelaufen');
    }

    // Find the selected option
    const optionIndex = poll.options.findIndex(opt => opt.id === selectedOptionId);
    if (optionIndex === -1) throw new Error('Ungültige Option');

    // Check if user already voted (if not allowing multiple votes)
    if (!poll.settings.allowMultipleVotes) {
      const hasVoted = poll.options.some(option => 
        option.votes.some(vote => vote.voter === voterData.name)
      );
      if (hasVoted) {
        throw new Error('Sie haben bereits abgestimmt');
      }
    }

    // Add vote
    poll.options[optionIndex].votes.push({
      id: uuidv4(),
      voter: voterData.name || 'Anonym',
      email: voterData.email || '',
      votedAt: new Date().toISOString()
    });

    poll.updatedAt = new Date().toISOString();
    polls[pollIndex] = poll;
    localStorage.setItem(POLLS_KEY, JSON.stringify(polls));

    return poll;
  }

  async deletePoll(id) {
    const currentUser = await authService.getCurrentUser();
    if (!currentUser) throw new Error('Nicht authentifiziert');

    const polls = JSON.parse(localStorage.getItem(POLLS_KEY));
    const pollIndex = polls.findIndex(p => p.id === id && p.ownerId === currentUser.id);
    
    if (pollIndex === -1) throw new Error('Umfrage nicht gefunden');

    // Mark as inactive instead of deleting (for data integrity)
    polls[pollIndex].isActive = false;
    polls[pollIndex].deletedAt = new Date().toISOString();

    localStorage.setItem(POLLS_KEY, JSON.stringify(polls));
    return true;
  }

  async cleanupExpiredData() {
    const polls = JSON.parse(localStorage.getItem(POLLS_KEY) || '[]');
    const now = new Date();
    
    const activePolls = polls.filter(poll => {
      if (poll.deletedAt) {
        const deletedDate = new Date(poll.deletedAt);
        const threeYearsAfterDeletion = new Date(deletedDate);
        threeYearsAfterDeletion.setFullYear(threeYearsAfterDeletion.getFullYear() + 3);
        return now < threeYearsAfterDeletion;
      }
      
      if (poll.settings.deadline) {
        const deadlineDate = new Date(poll.settings.deadline);
        const threeYearsAfterDeadline = new Date(deadlineDate);
        threeYearsAfterDeadline.setFullYear(threeYearsAfterDeadline.getFullYear() + 3);
        return now < threeYearsAfterDeadline;
      }
      
      return true;
    });

    localStorage.setItem(POLLS_KEY, JSON.stringify(activePolls));
  }
}

export const pollService = new PollService();